import React from "react";
import { ReactSVG } from "react-svg";
import { PreviewCloudinaryImage } from "../../PreviewCloudinaryImage";
import { HERO_HEADER_VARIANTS, ICON_PATHS, type HeroHeaderVariant } from "../constants";

interface UseLogoRendererParams {
  variant: HeroHeaderVariant;
  logoUrl?: string;
  isInitialPreview?: boolean;
  logoAlt?: string;
  title?: string;
}

/**
 * Renders logos only for variants without dedicated sub-components.
 *
 * BUSINESS and VCARD handle their own logos via:
 * - BusinessHeaderVariant.tsx
 * - VcardHeaderVariant.tsx
 *
 * This hook handles: WEBSITE, APPS, LINKS
 */
export const useLogoRenderer = ({
  variant,
  logoUrl,
  isInitialPreview,
  logoAlt,
  title,
}: UseLogoRendererParams) => {
  const renderLogo = React.useCallback(() => {
    if (!logoUrl) {
      return null;
    }

    switch (variant) {
      case HERO_HEADER_VARIANTS.WEBSITE:
        return (
          <div className="preview-hero-header__logo-container">
            <PreviewCloudinaryImage
              src={logoUrl}
              alt={logoAlt || title || "Website logo"}
              variant="hero"
              isInitPreview={isInitialPreview}
              className="preview-hero-header__logo"
              placeholderClass="website-logo--background"
            />
          </div>
        );

      case HERO_HEADER_VARIANTS.APPS:
        return (
          <div className="preview-hero-header__logo-container">
            <PreviewCloudinaryImage
              src={logoUrl}
              alt={logoAlt || title || "App logo"}
              isInitPreview={isInitialPreview}
              className="preview-hero-header__logo"
              placeholderClass="preview-hero-header__logo"
            />
          </div>
        );

      case HERO_HEADER_VARIANTS.LINKS:
        return (
          <div className="preview-hero-header__logo-container">
            <PreviewCloudinaryImage
              src={logoUrl}
              alt={logoAlt || title || "Link preview"}
              variant="hero"
              isInitPreview={isInitialPreview}
              className="preview-hero-header__logo"
            />
          </div>
        );

      case HERO_HEADER_VARIANTS.BUSINESS:
      case HERO_HEADER_VARIANTS.VCARD:
        return null;

      default:
        return null;
    }
  }, [variant, logoUrl, isInitialPreview, logoAlt, title]);

  const renderAppsPlaceholder = React.useCallback(() => {
    if (variant !== HERO_HEADER_VARIANTS.APPS || logoUrl || !isInitialPreview) {
      return null;
    }

    return (
      <div className="preview-hero-header__placeholder">
        <div className="preview-hero-header__placeholder-wrapper">
          <ReactSVG
            className="preview-hero-header__placeholder-icon"
            src={ICON_PATHS.IMAGE_PLACEHOLDER}
          />
        </div>
      </div>
    );
  }, [variant, logoUrl, isInitialPreview]);

  return {
    renderLogo,
    renderAppsPlaceholder,
  };
};
